import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, mergeMap, Observable } from 'rxjs';
import {environment} from "../../../environments/environment";
import {Router} from "@angular/router";
import {TokenStorageService} from "../auth/token-storage.service";
import { KeycloakSessionService } from "../auth/keycloak-session.service";
import { isBearerTarget } from "./first-party-api";
import { expireSession, sessionExpiredError } from "./session-expired";

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private tokenStorage: TokenStorageService,
    private readonly router: Router,
    private readonly keycloakSession: KeycloakSessionService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (environment.UNSECURE) {
      return next.handle(request);
    }

    // The Bearer token goes only to first-party hosts, and never over an Authorization header the caller set.
    if (!isBearerTarget(request.url) || request.headers.has(TOKEN_HEADER_KEY)) {
      return next.handle(request);
    }

    return from(this.prepareRequest(request)).pipe(
      mergeMap(preparedRequest => next.handle(preparedRequest))
    );
  }

  private async prepareRequest(request: HttpRequest<any>): Promise<HttpRequest<any>> {
    const hasStoredToken = !!this.tokenStorage.getToken();

    if (!hasStoredToken) {
      console.debug('[AuthInterceptor] No token — request sent without Authorization header:', request.url);
      return request;
    }

    const token = await this.keycloakSession.getValidToken(30, `Before request: ${request.url}`);
    if (!token) {
      // Fail the request instead of dropping it, otherwise callers never get next/error and spinners hang.
      if (this.keycloakSession.isSessionDefinitivelyExpired()) {
        console.debug('[AuthInterceptor] Token expired — clearing session and redirecting to login');
        expireSession(this.keycloakSession, this.router);
        throw sessionExpiredError(request.url);
      }

      // Refresh failed but keycloak still holds a refresh token: keep the session, a later request retries.
      console.warn('[AuthInterceptor] Token refresh failed, request not sent:', request.url);
      throw new HttpErrorResponse({ status: 0, url: request.url, statusText: 'Token refresh failed' });
    }

    console.debug('[AuthInterceptor] Using valid token for request:', request.url);

    return request.clone({
      headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${token}`)
    });
  }
}
